import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AlertTriangle,
  ChevronDown,
  CreditCard,
  ExternalLink,
  Gift,
  KeyRound,
  LayoutDashboard,
  Lock,
  LogOut,
  Mail,
  Menu,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  Tag,
  Trash2,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { FormField } from '@/components/ui/FormField';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { Toggle } from '@/components/ui/Toggle';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AffiliateSection } from '@/components/account/AffiliateSection';
import { AffiliatePayoutsCard } from '@/components/account/AffiliatePayoutsCard';
// ReferralSection (the open "earn free months" program) is intentionally not
// rendered — the referral concept is hidden for now; the affiliate program
// stays. Import removed rather than deleting the component so it's a one-line
// restore if we bring referrals back.
import { AccountPinGate } from '@/components/account/AccountPinGate';
import { PinInput } from '@/components/account/PinInput';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { cn } from '@/lib/cn';
import { isTrialActive, trialDaysRemaining } from '@/lib/trial';
import { PLANS, priceCopyFor } from '@/constants/plans';
import { accountsService } from '@/services/accounts.service';
import { billingService } from '@/services/billing.service';
import { env } from '@/lib/env';
import { useAccountStore, formatAccountName } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import { useSessionStore } from '@/store/session.store';
import { ApiError } from '@/services/http';
import type { SubscriptionDiscountView, SubscriptionView } from '@/types/api';

/**
 * Which settings group the left submenu is showing. The page is one route
 * (`/account`) with a client-side vertical submenu — switching is purely
 * local UI state, no navigation.
 */
type AccountSectionKey =
  | 'account'
  | 'billing'
  | 'affiliate'
  | 'safety'
  | 'notifications'
  | 'admin';

/**
 * Account — `/account`. Edits the tenant-level profile (first + last name)
 * and surfaces the read-only Auth0 identity (email) so parents can tell
 * which login they're signed in as. Settings are grouped into a left-side
 * vertical submenu (Account Info / Plans & Billing / Affiliate /
 * Account Safety, plus Admin for operators). Sign out lives in the submenu
 * footer and clears every persisted store before handing off to Auth0 so a
 * subsequent login starts from a clean slate.
 */
export function AccountPage() {
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const account = useAccountStore((s) => s.account);
  const isAdmin = useAccountStore((s) => s.isAdmin());
  const setAccount = useAccountStore((s) => s.setAccount);
  const patchAccount = useAccountStore((s) => s.patchAccount);
  const setSubscription = useAccountStore((s) => s.setSubscription);
  const resetAccount = useAccountStore((s) => s.reset);
  const resetReaders = useReadersStore((s) => s.reset);
  const resetSession = useSessionStore((s) => s.reset);

  const [firstName, setFirstName] = useState(account?.firstName ?? '');
  const [lastName, setLastName] = useState(account?.lastName ?? '');
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [commentsSaving, setCommentsSaving] = useState(false);
  // Which email-pref toggle (if any) is mid-write — disables both while one
  // is saving so a rapid double-tap can't race the optimistic update.
  const [savingEmailPref, setSavingEmailPref] = useState<'marketing' | 'account' | null>(null);
  const [activeSection, setActiveSection] = useState<AccountSectionKey>('account');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  // Parent-PIN gate. In-memory so the PIN is required on every visit
  // (navigating away unmounts the page and re-locks it).
  const [unlocked, setUnlocked] = useState(false);

  // Keep the form in sync when the cached account resolves / changes.
  useEffect(() => {
    if (!account) return;
    setFirstName(account.firstName);
    setLastName(account.lastName);
  }, [account]);

  // Refetch the authoritative account on mount and reconcile the cache.
  //
  // The persisted `account-store` is only refreshed at bootstrap
  // (`POST /auth/callback` in `ProtectedRoute`), and that bootstrap is
  // skipped on reload once `accountBootstrapped` is set — so any field
  // that changes out-of-band stays stale in localStorage until the user
  // logs out. The concrete symptom: an admin approves an affiliate
  // (`affiliate_status` → `approved` in the DB), but the affiliate's
  // Account page keeps rendering "Request received — under review" from
  // the cached `pending`. Subscription state (mutated by Stripe webhooks)
  // has the same staleness class. Refetching `/accounts/me` here makes the
  // page authoritative on every visit. Best-effort — the cached copy is a
  // fine fallback if the request fails, so we don't surface an error.
  useEffect(() => {
    let alive = true;
    accountsService
      .getMe()
      .then((fresh) => {
        if (alive) setAccount(fresh);
      })
      .catch(() => {
        // Silent — keep rendering from the persisted cache.
      });
    return () => {
      alive = false;
    };
  }, [setAccount]);

  // After Stripe Checkout: refetch the subscription slice so the card
  // reflects the new tier even before the webhook lands. We do NOT block
  // on this — the webhook is the source of truth, this is just a UX
  // niceness so the user doesn't see "Free" for 30 seconds while
  // celebrating a paid upgrade. Also strip the query param so a refresh
  // doesn't re-fire the toast.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    if (checkout === 'success') {
      toast.success('Welcome to your new plan!', {
        description: 'It may take a moment for your subscription to appear.',
      });
      void billingService
        .getSubscription()
        .then((sub: SubscriptionView) => setSubscription(sub))
        .catch(() => {
          // Silent — the next page load will pick up the webhook-applied
          // state via /accounts/me. Don't error-toast here; the user just
          // had a successful payment.
        });
    } else if (checkout === 'canceled') {
      toast.message('Checkout canceled', {
        description: 'No worries — your plan is unchanged.',
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setSubscription]);

  // After Stripe Connect onboarding: toast the user and strip the param.
  // The AffiliatePayoutsCard re-fetches its status on mount, so by the
  // time this effect runs the card is already showing whatever bucket
  // Stripe landed the affiliate in.
  useEffect(() => {
    const connect = searchParams.get('connect');
    if (!connect) return;
    if (connect === 'return') {
      toast.success('Stripe onboarding saved.', {
        description: 'We’ll verify your details and let you know when payouts are ready.',
      });
    } else if (connect === 'refresh') {
      toast.message('Need to finish setting up.', {
        description: 'The Stripe link expired — start onboarding again to continue.',
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const hasChanges =
    account !== null &&
    (firstName.trim() !== account.firstName || lastName.trim() !== account.lastName);

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      toast.error('First and last name are required.');
      return;
    }
    if (!account) return;

    setSaving(true);
    try {
      const updated = await accountsService.updateMe({
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });
      // Use `patchAccount` instead of `setAccount` so we only touch the
      // fields the server echoed back — keeps any future locally-added
      // derived fields intact.
      patchAccount(updated);
      toast.success('Your profile has been updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComments = async (next: boolean) => {
    if (!account || commentsSaving) return;
    // Optimistic flip — the toggle is interactive and should feel
    // instant. We rollback on failure to keep the UI honest.
    const previous = account.commentsEnabled;
    patchAccount({ commentsEnabled: next });
    setCommentsSaving(true);
    try {
      const updated = await accountsService.updateMe({ commentsEnabled: next });
      patchAccount(updated);
      toast.success(next ? 'Comments turned on.' : 'Comments turned off.');
    } catch (err) {
      patchAccount({ commentsEnabled: previous });
      toast.error(err instanceof ApiError ? err.message : 'Could not update comments setting.');
    } finally {
      setCommentsSaving(false);
    }
  };

  const handleToggleMarketing = async (next: boolean) => {
    if (!account || savingEmailPref) return;
    const previous = account.marketingEmailsEnabled;
    patchAccount({ marketingEmailsEnabled: next });
    setSavingEmailPref('marketing');
    try {
      const updated = await accountsService.updateMe({ marketingEmailsEnabled: next });
      patchAccount(updated);
      toast.success(next ? 'Marketing emails turned on.' : 'Marketing emails turned off.');
    } catch (err) {
      patchAccount({ marketingEmailsEnabled: previous });
      toast.error(err instanceof ApiError ? err.message : 'Could not update setting.');
    } finally {
      setSavingEmailPref(null);
    }
  };

  const handleToggleAccountNotifs = async (next: boolean) => {
    if (!account || savingEmailPref) return;
    const previous = account.accountNotificationsEnabled;
    patchAccount({ accountNotificationsEnabled: next });
    setSavingEmailPref('account');
    try {
      const updated = await accountsService.updateMe({ accountNotificationsEnabled: next });
      patchAccount(updated);
      toast.success(next ? 'Account notifications turned on.' : 'Account notifications turned off.');
    } catch (err) {
      patchAccount({ accountNotificationsEnabled: previous });
      toast.error(err instanceof ApiError ? err.message : 'Could not update setting.');
    } finally {
      setSavingEmailPref(null);
    }
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await billingService.createPortalSession();
      window.location.assign(url);
    } catch (err) {
      setPortalLoading(false);
      toast.error(err instanceof ApiError ? err.message : 'Could not open the billing portal.');
    }
  };

  /**
   * Triggers Auth0's email-based password change flow. We hit the
   * `/dbconnections/change_password` endpoint directly from the browser
   * with the user's logged-in email — Auth0 then emails a one-time reset
   * link to that address and the user completes the change on Auth0's
   * hosted page.
   *
   * This deliberately doesn't ask for the current password in our UI:
   *   - We'd otherwise have to handle plaintext passwords and proxy them
   *     to Auth0's Management API, which means provisioning a server-side
   *     M2M token and adding more PII into our request logs.
   *   - The email loop verifies inbox control, which is a strictly
   *     stronger guarantee than re-typing a password the user is already
   *     authenticated with.
   *
   * The endpoint is intended for anonymous use (it's the same one the
   * "Forgot password?" link on Auth0's login page calls) so no Bearer
   * token is required. The email is locked to the authenticated user, so
   * a hijacked button can't be used to spam other accounts' inboxes.
   */
  const handleChangePassword = async () => {
    if (!user?.email) {
      toast.error('No email on file — sign out and back in to refresh your session.');
      return;
    }
    if (!env.auth0Domain || !env.auth0ClientId) {
      toast.error('Auth0 is not configured on this build.');
      return;
    }
    setPasswordResetLoading(true);
    try {
      const response = await fetch(`https://${env.auth0Domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: env.auth0ClientId,
          email: user.email,
          connection: env.auth0DbConnection,
        }),
      });
      if (!response.ok) {
        // Auth0 returns plain text on this endpoint for both success and
        // most error cases. Surface whatever it sent so we don't swallow
        // useful diagnostics like "Connection X not found".
        const message = (await response.text().catch(() => '')) || `HTTP ${response.status}`;
        throw new Error(message);
      }
      toast.success('Check your inbox', {
        description: `We sent a password reset link to ${user.email}.`,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start the password reset.');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear every persisted store before Auth0 tears down the session.
    // The order matters: reset state first (so any React subscribers
    // unmount cleanly), then hand off to Auth0 which triggers a full
    // page redirect.
    resetReaders();
    resetAccount();
    resetSession();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleDeleteAccount = async () => {
    if (deletingAccount) return;
    setDeletingAccount(true);
    try {
      await accountsService.deleteMe();
      toast.success('Your account has been deleted.');
      // The account no longer exists — clear local state and sign out, which
      // hands off to Auth0 and lands back on /login.
      handleLogout();
    } catch (err) {
      // 409 = a still-renewing subscription must be canceled first (the UI
      // already gates the button, but the server is the source of truth).
      setDeletingAccount(false);
      setConfirmDelete(false);
      toast.error(err instanceof ApiError ? err.message : 'Could not delete your account.');
    }
  };

  const displayName = formatAccountName(account);
  const subscription = account?.subscription ?? null;
  // Self-delete is gated on canceling first: blocked while a still-renewing
  // subscription is active; one already set to cancel at period end is allowed.
  const mustCancelSubscriptionFirst =
    (subscription?.isPaid ?? false) && !(subscription?.cancelAtPeriodEnd ?? false);
  // Admins are treated as affiliates so they can exercise the cash dashboard
  // while testing — mirrors the AffiliateSection / server-guard bypass.
  const isApprovedAffiliate = account?.affiliateStatus === 'approved' || isAdmin;

  // Left submenu items. "Admin" only appears for operators; "Sign out" is
  // rendered separately in the nav footer (it's an action, not a panel).
  const navItems: { key: AccountSectionKey; label: string; icon: LucideIcon }[] = [
    { key: 'account', label: 'Account Info', icon: UserRound },
    { key: 'billing', label: 'Plans & Billing', icon: CreditCard },
    { key: 'affiliate', label: 'Affiliate', icon: Gift },
    { key: 'safety', label: 'Account Safety', icon: ShieldCheck },
    { key: 'notifications', label: 'Email Notifications', icon: Mail },
    ...(isAdmin ? [{ key: 'admin' as const, label: 'Admin', icon: LayoutDashboard }] : []),
  ];

  // Auth0's `sub` is `<connection-strategy>|<id>`. Database (email/password)
  // users start with `auth0|`; social logins use the provider's name, e.g.
  // `google-oauth2|...`. We only show the password change button to the
  // former — social users don't have a password on our side; theirs lives
  // with the upstream identity provider.
  const isPasswordUser = (user?.sub ?? '').startsWith('auth0|');
  const socialProviderLabel = (() => {
    const sub = user?.sub ?? '';
    if (sub.startsWith('google-oauth2|')) return 'Google';
    if (sub.startsWith('apple|')) return 'Apple';
    if (sub.startsWith('facebook|')) return 'Facebook';
    return 'your identity provider';
  })();

  // Wait for the account to hydrate before deciding the PIN gate — we need
  // `hasParentPin` to know whether to verify an existing PIN or force a setup.
  if (!account) {
    return (
      <AppShell mode="parent">
        <div className="flex justify-center py-12">
          <InlineLoader label="Loading your account…" />
        </div>
      </AppShell>
    );
  }

  // Gate the whole page behind the parent PIN: verify it if one exists, or
  // require the family to set one first. Unlock is in-memory (see `unlocked`).
  if (!unlocked) {
    return (
      <AppShell mode="parent">
        <div className="flex flex-col gap-6">
          <SectionTitle kicker="Account" heading="Your profile" />
          <AccountPinGate hasPin={account.hasParentPin} onUnlock={() => setUnlocked(true)} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <SectionTitle kicker="Account" heading="Your profile" />

        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          {/* Mobile submenu — a "Menu" dropdown instead of a horizontal
              scroll strip. Hidden at md+ where the vertical sidebar shows. */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label="Account settings menu"
                className="flex w-full items-center gap-2.5 rounded-xl border border-or-slate-200 bg-white px-3.5 py-2.5 text-left text-[13px] font-semibold text-or-slate-700 transition hover:bg-or-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40 md:hidden"
              >
                <Menu className="h-4 w-4 shrink-0" aria-hidden />
                Menu
                <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-or-slate-400" aria-hidden />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                sideOffset={8}
                className="z-50 w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-2xl border border-or-slate-200 bg-white p-1 shadow-lg md:hidden"
              >
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = activeSection === item.key;
                  return (
                    <DropdownMenu.Item
                      key={item.key}
                      onSelect={() => setActiveSection(item.key)}
                      className={cn(
                        'flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-[13px] font-semibold outline-none transition',
                        active
                          ? 'bg-or-orange-100 text-or-orange-800'
                          : 'text-or-slate-600 data-[highlighted]:bg-or-slate-100',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      {item.label}
                    </DropdownMenu.Item>
                  );
                })}
                <DropdownMenu.Separator className="my-1 h-px bg-or-slate-100" />
                <DropdownMenu.Item
                  onSelect={() => handleLogout()}
                  className="flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-or-slate-600 outline-none transition data-[highlighted]:bg-or-rose-100 data-[highlighted]:text-or-rose-700"
                >
                  <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          {/* Vertical submenu — picks which settings group shows on the
              right. Shown at md+; on mobile the Menu dropdown above takes
              its place. */}
          <nav
            aria-label="Account settings"
            className="hidden gap-1 md:flex md:w-56 md:shrink-0 md:flex-col"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = activeSection === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActiveSection(item.key)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold transition',
                    active
                      ? 'bg-or-orange-100 text-or-orange-800'
                      : 'text-or-slate-600 hover:bg-or-slate-100',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  {item.label}
                </button>
              );
            })}

            <div className="my-1 hidden border-t border-or-slate-100 md:block" />
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold text-or-slate-600 transition hover:bg-or-rose-100 hover:text-or-rose-700"
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              Sign out
            </button>
          </nav>

          <div className="flex min-w-0 flex-1 flex-col gap-6">
            {/* ── Account Info — name, email, password ────────────────── */}
            {activeSection === 'account' ? (
              <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
                <div className="font-display text-[16px] font-bold text-or-slate-900">
                  Signed in as {displayName}
                </div>
                <p className="mt-1 text-[13px] text-or-slate-600">
                  Update the name that appears across OllieReads. Your login email is managed by
                  Auth0 and can't be changed here.
                </p>
                <form onSubmit={handleSave} className="mt-5 flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      label="First name"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      maxLength={120}
                    />
                    <FormField
                      label="Last name"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      maxLength={120}
                    />
                  </div>
                  <FormField
                    label="Email"
                    value={user?.email ?? ''}
                    readOnly
                    disabled
                    help="Your email is managed by your Auth0 identity. Change it from your identity provider."
                  />
                  <div className="flex items-center justify-end">
                    <Button type="submit" size="md" disabled={saving || !hasChanges}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                  </div>
                </form>

                {/* Password change — surfaced inside the same section so
                    identity controls (name, email, password) live together. */}
                <div className="mt-6 border-t border-or-slate-100 pt-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="max-w-md">
                      <div className="font-display text-[14px] font-bold text-or-slate-900">
                        Password
                      </div>
                      <p className="mt-1 text-[13px] text-or-slate-600">
                        {isPasswordUser
                          ? "We'll email you a secure link to set a new password. Your current password keeps working until you complete the change."
                          : `You signed in with ${socialProviderLabel}, so your password is managed there. To change it, visit your ${socialProviderLabel} account settings.`}
                      </p>
                    </div>
                    {isPasswordUser ? (
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={handleChangePassword}
                        disabled={passwordResetLoading}
                      >
                        <KeyRound className="h-4 w-4" aria-hidden />
                        {passwordResetLoading ? 'Sending…' : 'Change password'}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </section>
            ) : null}

            {/* ── Plans & Billing ─────────────────────────────────────── */}
            {activeSection === 'billing' ? (
              <SubscriptionCard
                subscription={subscription}
                portalLoading={portalLoading}
                onUpgrade={() => navigate('/pricing')}
                onManage={handleOpenPortal}
              />
            ) : null}

            {/* ── Affiliate ───────────────────────────────────────────────
                The open referral program (free Ollie Family months) is hidden
                for now; only the affiliate CASH program shows. Approved
                affiliates (and admins) get the dashboard + Stripe payouts;
                everyone else sees the "Request to Become an Affiliate" card. */}
            {activeSection === 'affiliate' ? (
              <>
                <AffiliateSection />
                {isApprovedAffiliate ? <AffiliatePayoutsCard /> : null}
              </>
            ) : null}

            {/* ── Account Safety — parental controls ──────────────────────
                Comments toggle is server-authoritative (`ReviewsService`
                rejects submissions while off); parent PIN gates off-site
                links. */}
            {activeSection === 'safety' ? (
              <>
                <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="max-w-md">
                      <div className="font-display text-[16px] font-bold text-or-slate-900">
                        Comments &amp; reviews
                      </div>
                      <p className="mt-1 text-[13px] text-or-slate-600">
                        Show book comments to your readers, and let them post their own. Star
                        ratings are always visible so the global average reflects everyone's reads.
                      </p>
                    </div>
                    <span
                      aria-hidden
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
                    >
                      <MessageSquareText className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-3">
                    <div>
                      <div className="font-display text-[14px] font-bold text-or-slate-900">
                        Show book comments
                      </div>
                      <p className="mt-0.5 text-[12px] text-or-slate-600">
                        Turn off to hide other readers' comments and stop new comments from this
                        account.
                      </p>
                    </div>
                    <Toggle
                      checked={account?.commentsEnabled !== false}
                      disabled={!account || commentsSaving}
                      onChange={handleToggleComments}
                      label="Show book comments"
                    />
                  </div>
                </section>

                <ParentPinSection />

                {/* ── Danger zone — permanently delete this account ── */}
                <section className="rounded-2xl border border-or-rose-100 bg-white p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="max-w-md">
                      <div className="font-display text-[16px] font-bold text-or-rose-700">
                        Delete account
                      </div>
                      <p className="mt-1 text-[13px] text-or-slate-600">
                        Permanently delete your account and everything in it — every reader
                        profile, bookmark, reading-progress record, review, coin balance, and
                        reward.{' '}
                        <strong className="font-semibold text-or-slate-900">
                          This can&rsquo;t be undone.
                        </strong>
                      </p>
                    </div>
                    <span
                      aria-hidden
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-or-rose-100 text-or-rose-700"
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </span>
                  </div>

                  {mustCancelSubscriptionFirst ? (
                    <div className="mt-4 rounded-xl border border-or-rose-100 bg-or-rose-100/40 px-4 py-3">
                      <div className="font-display text-[14px] font-bold text-or-slate-900">
                        Cancel your subscription first
                      </div>
                      <p className="mt-0.5 text-[12px] text-or-slate-600">
                        You have an active subscription. Cancel it before deleting your account
                        so you&rsquo;re not charged again — then come back here to finish.
                      </p>
                      <Button
                        variant="secondary"
                        className="mt-3"
                        onClick={handleOpenPortal}
                        disabled={portalLoading}
                      >
                        {portalLoading ? 'Opening…' : 'Manage subscription'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="danger"
                      className="mt-4"
                      onClick={() => setConfirmDelete(true)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      Delete my account
                    </Button>
                  )}
                </section>

                <ConfirmDialog
                  open={confirmDelete}
                  onOpenChange={(open) => {
                    if (!open) setConfirmDelete(false);
                  }}
                  title="Delete your account?"
                  description="This permanently deletes your account and all of its data — reader profiles, bookmarks, reading progress, reviews, coins, and rewards. This can't be undone."
                  confirmLabel="Delete my account"
                  onConfirm={handleDeleteAccount}
                  isConfirming={deletingAccount}
                />
              </>
            ) : null}

            {/* ── Email Notifications — marketing + account email toggles ── */}
            {activeSection === 'notifications' ? (
              <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-[16px] font-bold text-or-slate-900">
                      Email Notifications
                    </div>
                    <p className="mt-1 text-[13px] text-or-slate-600">
                      Choose which emails OllieReads can send you. You can change these anytime.
                    </p>
                  </div>
                  <span
                    aria-hidden
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
                  >
                    <Mail className="h-4 w-4" />
                  </span>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-3">
                  <div>
                    <div className="font-display text-[14px] font-bold text-or-slate-900">
                      Marketing Communication
                    </div>
                    <p className="mt-0.5 text-[12px] text-or-slate-600">
                      News, tips, and special offers from OllieReads.
                    </p>
                  </div>
                  <Toggle
                    checked={account?.marketingEmailsEnabled !== false}
                    disabled={!account || savingEmailPref !== null}
                    onChange={handleToggleMarketing}
                    label="Marketing Communication"
                  />
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-3">
                  <div>
                    <div className="font-display text-[14px] font-bold text-or-slate-900">
                      Account Notifications
                    </div>
                    <p className="mt-0.5 text-[12px] text-or-slate-600">
                      Important updates about your account, subscription, and security.
                    </p>
                  </div>
                  <Toggle
                    checked={account?.accountNotificationsEnabled !== false}
                    disabled={!account || savingEmailPref !== null}
                    onChange={handleToggleAccountNotifs}
                    label="Account Notifications"
                  />
                </div>
              </section>
            ) : null}

            {/* ── Admin tools — operator dashboard entry (admin-only) ──── */}
            {activeSection === 'admin' && isAdmin ? (
              <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-[16px] font-bold text-or-slate-900">
                      Admin tools
                    </div>
                    <p className="mt-1 text-[13px] text-or-slate-600">
                      You have operator access. Manage the book catalog, labels, and Learn With
                      Ollie prompts from the admin dashboard.
                    </p>
                  </div>
                  <Chip variant="live">Admin</Chip>
                </div>
                <Button variant="secondary" className="mt-4" onClick={() => navigate('/admin')}>
                  <ShieldCheck className="h-4 w-4" aria-hidden />
                  Open admin dashboard
                </Button>
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

interface SubscriptionCardProps {
  subscription: SubscriptionView | null;
  portalLoading: boolean;
  onUpgrade: () => void;
  onManage: () => void;
}

/**
 * Subscription card on `/account`. Two visual states:
 *
 *   - Free tier (no `isPaid`): "Upgrade to a paid plan" CTA → `/pricing`.
 *   - Paid tier: shows current plan, renewal date, cancel-at-period-end
 *     warning, and a "Manage subscription" button → Customer Portal.
 *
 * Status badge color tracks `subscription.status`:
 *   - active/trialing → green
 *   - past_due → yellow (still considered paid; dunning grace window)
 *   - anything else → slate (informational)
 *
 * We deliberately keep this small. All subscription state changes go
 * through Stripe's hosted portal — we don't reimplement cancel/upgrade
 * forms here.
 */
function SubscriptionCard({
  subscription,
  portalLoading,
  onUpgrade,
  onManage,
}: SubscriptionCardProps) {
  const tier = subscription?.planTier ?? 'free';
  const plan = PLANS[tier];
  const isPaid = subscription?.isPaid ?? false;

  // Any coupon/promo applied to the Stripe subscription. Fetched on mount
  // (paid subscribers only) because discounts aren't mirrored locally — the
  // server round-trips to Stripe. Best-effort: on failure we simply omit the
  // line, so the card never breaks on a Stripe hiccup.
  const [discount, setDiscount] = useState<SubscriptionDiscountView | null>(null);
  useEffect(() => {
    if (!isPaid) {
      setDiscount(null);
      return;
    }
    let alive = true;
    billingService
      .getDiscount()
      .then((d) => {
        if (alive) setDiscount(d);
      })
      .catch(() => {
        if (alive) setDiscount(null);
      });
    return () => {
      alive = false;
    };
  }, [isPaid]);
  // When Stripe has the subscription scheduled to cancel at period end, we
  // surface that as a distinct state in the UI: the status chip switches to
  // "Pending Cancellation" (overriding the raw Stripe `active` status — which
  // is technically accurate but misleading to a user who just hit Cancel),
  // and a prominent banner at the top of the card spells out the exact date
  // they'll drop back to free. Both reads are driven off the same two
  // webhook-mirrored columns: `subscription_cancel_at_period_end` and
  // `subscription_current_period_end`.
  const isPendingCancellation = isPaid && (subscription?.cancelAtPeriodEnd ?? false);
  const cancellationDate = formatPeriodEnd(subscription?.currentPeriodEnd ?? null);

  // App-level free trial: full premium access on the `free` tier (no Stripe
  // sub), so it presents as its own state distinct from both paid and free.
  // Recomputed against the clock (not the persisted boolean) so an expired-
  // but-still-cached trial correctly falls back to the free state.
  const isInTrial = isTrialActive(subscription);
  const trialEndsDate = formatPeriodEnd(subscription?.trialEndsAt ?? null);
  const trialDaysLeft = trialDaysRemaining(subscription?.trialEndsAt ?? null);
  const trialDaysLabel = trialDaysLeft === 1 ? '1 day left' : `${trialDaysLeft} days left`;

  return (
    <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display text-[16px] font-bold text-or-slate-900">Subscription</div>
          <p className="mt-1 text-[13px] text-or-slate-600">
            {isPaid
              ? "You're on a paid plan. Manage billing through Stripe."
              : isInTrial
                ? 'Your free trial unlocks every premium feature. Pick a plan to keep them.'
                : "You're on the free plan. Upgrade for more readers and features."}
          </p>
        </div>
        {isPendingCancellation ? (
          <Chip variant="pending">Pending Cancellation</Chip>
        ) : isInTrial ? (
          <Chip variant="live">Free trial</Chip>
        ) : subscription?.status ? (
          <Chip variant={chipVariantForStatus(subscription.status)}>
            {humanizeStatus(subscription.status)}
          </Chip>
        ) : null}
      </div>

      {isPendingCancellation ? (
        <div
          role="status"
          className="mt-4 flex items-start gap-3 rounded-xl border border-or-orange-200 bg-or-orange-50 p-4"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-or-orange-600" aria-hidden />
          <div className="flex flex-col gap-1">
            <div className="font-display text-[14px] font-bold text-or-orange-900">
              Pending Cancellation — {cancellationDate}
            </div>
            <p className="text-[13px] text-or-orange-800">
              You've cancelled your {plan.name} subscription. You'll keep access until{' '}
              {cancellationDate}, after which your account will drop to the free plan. You can
              reactivate any time from the billing portal before that date.
            </p>
          </div>
        </div>
      ) : null}

      {isInTrial ? (
        <div
          role="status"
          className="mt-4 flex items-start gap-3 rounded-xl border border-or-orange-200 bg-or-orange-50 p-4"
        >
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-or-orange-600" aria-hidden />
          <div className="flex flex-col gap-1">
            <div className="font-display text-[14px] font-bold text-or-orange-900">
              Free trial · {trialDaysLabel}
            </div>
            <p className="text-[13px] text-or-orange-800">
              You have full access to every premium feature until {trialEndsDate}. Pick a plan now
              to keep quizzes, Ollie Learns, the Egg Shop, and more after your trial ends.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl border border-or-slate-100 bg-or-cream-50 p-4 sm:grid-cols-3">
        <Stat label="Plan" value={isInTrial ? 'Free trial' : plan.name} />
        <Stat
          label="Price"
          value={isInTrial ? '$0' : priceCopyFor(plan, subscription?.interval).priceLabel}
        />
        <Stat
          label={isInTrial ? 'Trial ends' : isPendingCancellation ? 'Ends' : 'Renews'}
          value={isInTrial ? trialEndsDate : cancellationDate}
        />
      </div>

      {discount ? (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-or-green-100 bg-or-green-100 px-4 py-3 text-[13px] text-or-slate-800">
          <Tag className="h-4 w-4 shrink-0 text-or-green-700" aria-hidden />
          <span>
            <span className="font-semibold">Discount applied:</span> {discount.label}
            {discount.durationLabel ? ` · ${discount.durationLabel}` : ''}
            {discount.name ? ` (${discount.name})` : ''}
          </span>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {isPaid ? (
          <Button variant="secondary" onClick={onManage} disabled={portalLoading}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            {portalLoading ? 'Opening…' : 'Manage subscription'}
          </Button>
        ) : (
          <Button onClick={onUpgrade}>
            <Sparkles className="h-4 w-4" aria-hidden />
            {isInTrial ? 'Pick a plan' : 'See plans'}
          </Button>
        )}
        {/* Even paid users get a "see plans" link — useful for switching
            tiers without going through the portal first. */}
        {isPaid ? (
          <Button variant="ghost" onClick={onUpgrade}>
            Compare plans
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-or-slate-500">
        {label}
      </div>
      <div className="font-display text-[15px] font-bold text-or-slate-900">{value}</div>
    </div>
  );
}

function humanizeStatus(status: string): string {
  // Stripe's enum is snake_case. Title-case it for display.
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function chipVariantForStatus(status: string): 'live' | 'pending' | 'default' {
  if (status === 'active' || status === 'trialing') return 'live';
  if (status === 'past_due') return 'pending';
  return 'default';
}

function formatPeriodEnd(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}


/**
 * ParentPinSection — set/change/remove the 4-digit gate used by the
 * "Buy on Amazon" link on book detail. The PIN is hashed (scrypt) on
 * the server before storage; the raw value never leaves this form.
 *
 * Two UI states:
 *   * No PIN set yet: a single "Set a PIN" CTA opens the digit grid.
 *   * PIN set: shows "PIN is on", a "Change PIN" button to re-enter,
 *     and a "Remove PIN" button (guarded by ConfirmDialog because
 *     removing exposes any future affiliate clicks).
 */
function ParentPinSection() {
  const account = useAccountStore((s) => s.account);
  const setAccount = useAccountStore((s) => s.setAccount);
  const hasPin = account?.hasParentPin === true;

  const [editing, setEditing] = useState(false);
  const [digits, setDigits] = useState<string[]>(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const startEditing = () => {
    setDigits(['', '', '', '']);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDigits(['', '', '', '']);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const pin = digits.join('');
    if (pin.length !== 4 || submitting) return;
    setSubmitting(true);
    try {
      const updated = await accountsService.setParentPin(pin);
      setAccount(updated);
      toast.success(hasPin ? 'Parent PIN updated.' : 'Parent PIN saved.');
      setEditing(false);
      setDigits(['', '', '', '']);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the PIN.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    setPendingRemove(false);
    setRemoving(true);
    try {
      const updated = await accountsService.clearParentPin();
      setAccount(updated);
      toast.success('Parent PIN removed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove the PIN.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-md">
          <div className="font-display text-[16px] font-bold text-or-slate-900">Parent PIN</div>
          <p className="mt-1 text-[13px] text-or-slate-600">
            A 4-digit PIN that gates off-site links (like the "Buy on Amazon" button on books) so
            your readers can't follow them without a grown-up.
          </p>
        </div>
        <span
          aria-hidden
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
        >
          <Lock className="h-4 w-4" />
        </span>
      </div>

      {editing ? (
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-4">
            <div className="font-display text-[14px] font-bold text-or-slate-900">
              {hasPin ? 'Pick a new PIN' : 'Choose a 4-digit PIN'}
            </div>
            <p className="mt-1 text-[12px] text-or-slate-600">
              Use something memorable — there's no recovery flow. You can always change it from this
              page.
            </p>
            <div className="mt-4">
              <PinInput
                digits={digits}
                onDigitsChange={setDigits}
                disabled={submitting}
                autoFocus
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button type="button" variant="secondary" onClick={cancelEditing} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={digits.join('').length !== 4 || submitting}>
              {submitting ? 'Saving…' : hasPin ? 'Update PIN' : 'Save PIN'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-3">
          <div className="text-[13px] text-or-slate-700">
            {hasPin ? (
              <>
                <span className="font-semibold text-or-slate-900">PIN is on.</span> Affiliate links
                will ask for it before opening.
              </>
            ) : (
              <>
                <span className="font-semibold text-or-slate-900">PIN is off.</span> "Buy on Amazon"
                links will prompt readers to set a PIN before opening.
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasPin ? (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setPendingRemove(true)}
                disabled={removing}
              >
                Remove PIN
              </Button>
            ) : null}
            <Button size="sm" onClick={startEditing} disabled={removing}>
              {hasPin ? 'Change PIN' : 'Set a PIN'}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingRemove}
        onOpenChange={(next) => {
          if (!next) setPendingRemove(false);
        }}
        title="Remove the parent PIN?"
        description="Without a PIN, the 'Buy on Amazon' link will pop a 'set a PIN first' dialog instead of opening the link. You can set a new PIN any time."
        confirmLabel="Remove PIN"
        confirmVariant="danger"
        onConfirm={handleRemove}
      />
    </section>
  );
}
